import { useState, useEffect, useCallback } from 'react';
import { NativePluginsService } from '../services/nativePluginsService';
import { getPlatformInfo, PlatformInfo } from '../utils/platformUtils';

export function useNativePlugins() {
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPlatformInfo = async () => {
      try {
        const info = await getPlatformInfo();
        setPlatformInfo(info);
      } catch (error) {
        console.error('Failed to load platform info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlatformInfo();
  }, []);

  const takePhoto = useCallback(async () => {
    return await NativePluginsService.takePhoto();
  }, []);

  const selectPhoto = useCallback(async () => {
    return await NativePluginsService.selectPhoto();
  }, []);

  const saveFile = useCallback(async (data: string, filename: string) => {
    return await NativePluginsService.saveFile(data, filename);
  }, []);

  const readFile = useCallback(async (filepath: string) => {
    return await NativePluginsService.readFile(filepath);
  }, []);

  const shareContent = useCallback(async (title: string, text: string, url?: string) => {
    return await NativePluginsService.shareContent(title, text, url);
  }, []);

  const showToast = useCallback(async (message: string, duration: 'short' | 'long' = 'short') => {
    return await NativePluginsService.showToast(message, duration);
  }, []);

  const triggerHaptic = useCallback(async (style: 'light' | 'medium' | 'heavy' = 'medium') => {
    const impactStyle = style === 'light' ? 'light' : style === 'heavy' ? 'heavy' : 'medium';
    return await NativePluginsService.triggerHaptic(impactStyle as any);
  }, []);

  const getAppState = useCallback(async () => {
    return await NativePluginsService.getAppState();
  }, []);

  const addAppStateListener = useCallback((callback: (state: { isActive: boolean }) => void) => {
    return NativePluginsService.addAppStateListener(callback);
  }, []);

  return {
    platformInfo,
    isLoading,
    isNative: platformInfo?.isNative ?? false,
    isWeb: !(platformInfo?.isNative ?? false),
    platform: platformInfo?.platform ?? 'web',
    isMobile: platformInfo?.isMobile ?? false,
    deviceInfo: platformInfo?.deviceInfo,
    // Plugin methods
    takePhoto,
    selectPhoto,
    saveFile,
    readFile,
    shareContent,
    showToast,
    triggerHaptic,
    getAppState,
    addAppStateListener,
  };
}
